import type { CellClassParams } from "ag-grid-community"

import type { PricingGridRow } from "@/screens/Pricing/components/tables/types"

export const getPricingRowKey = (row: Pick<PricingGridRow, "id" | "item">): string =>
  `${row.item.priceChangeItemId}::${row.id}`

// The same PriceChangeItem/tier can appear under more than one product+warehouse
// group, so the bare row key is only unique *within* a single grid. The edits store
// merges rows from every group, so its keys must additionally be scoped by group id.
export const getPriceChangeEditKey = (groupId: string, row: Pick<PricingGridRow, "id" | "item">): string =>
  `${groupId}::${getPricingRowKey(row)}`

export const isPricingRowModified = ({
  isUomChanged,
  modifiedRows,
  row,
}: {
  isUomChanged: boolean
  modifiedRows: Set<string>
  row: PricingGridRow
}): boolean => isUomChanged || modifiedRows.has(getPricingRowKey(row))

// Shared by the qty/effective-date columns — a required field only shows as
// missing once the row has actually been touched, so unedited server data isn't
// flagged before the user gets a chance to fill it in.
export const getRequiredEmptyCellClassRule =
  (
    isFieldEmpty: (row: PricingGridRow) => boolean,
    { readonly, modifiedRows, isUomChanged }: { readonly?: boolean; modifiedRows: Set<string>; isUomChanged: boolean }
  ) =>
  ({ data }: CellClassParams<PricingGridRow>): boolean => {
    if (!data) return !readonly

    return !readonly && isFieldEmpty(data) && isPricingRowModified({ isUomChanged, modifiedRows, row: data })
  }
